import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API, formatApiError } from "../auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";

export default function AdminBrands() {
  const { authHeaders } = useAuth();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/brands`, { headers: authHeaders() });
      setBrands(data);
    } catch (err) {
      console.error("Failed to load brands:", err);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "" });
    setOpen(true);
  };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ name: b.name, description: b.description || "" });
    setOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (!editing) {
        await axios.post(`${API}/brands`, form, { headers: authHeaders() });
        toast.success("Marca creada");
      } else {
        await axios.put(`${API}/brands/${editing.id}`, form, { headers: authHeaders() });
        toast.success("Marca actualizada");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSaving(false);
  };

  const del = async (b) => {
    try {
      await axios.delete(`${API}/brands/${b.id}`, { headers: authHeaders() });
      toast.success("Marca eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-[1400px] mx-auto" data-testid="admin-brands-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="kbd-label mb-2 text-emerald-700">/ Administración</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-950">Marcas</h1>
          <p className="text-slate-500 mt-2">Catálogo de marcas para los reportes de visita.</p>
        </div>
        <Button onClick={openCreate} className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2" data-testid="create-brand-btn">
          <Plus className="w-4 h-4" /> Nueva marca
        </Button>
      </div>

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center kbd-label text-slate-400">Cargando…</div>
      ) : brands.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-16 text-center">
          <Tag className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="font-display text-2xl text-slate-900 mb-2">Aún no hay marcas</p>
          <p className="text-sm text-slate-500">Crea la primera marca para empezar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((b) => (
            <div key={b.id} className="bg-white border border-slate-200 rounded-lg p-5 hover:border-emerald-600 transition-colors" data-testid={`brand-row-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="w-10 h-10 bg-emerald-100 text-emerald-800 font-display font-bold rounded-md flex items-center justify-center mb-3">
                    {b.name[0]?.toUpperCase()}
                  </div>
                  <h3 className="font-display font-bold text-lg text-slate-950 truncate">{b.name}</h3>
                  {b.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{b.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button variant="outline" size="sm" onClick={() => openEdit(b)} className="gap-2" data-testid={`edit-brand-${b.id}`}>
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2 text-red-700 border-red-200 hover:bg-red-50" data-testid={`delete-brand-${b.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> Eliminar
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar {b.name}?</AlertDialogTitle>
                      <AlertDialogDescription>Los reportes existentes mantendrán el nombre de la marca.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del(b)} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="brand-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar marca" : "Nueva marca"}</DialogTitle>
            <DialogDescription>Las marcas aparecen al crear reportes de visita.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="kbd-label">Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="brand-name" />
            </div>
            <div>
              <Label className="kbd-label">Descripción (opcional)</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2" data-testid="brand-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="save-brand-btn">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
